import { forwardRef, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";

export type PaperVariant = "default" | "muted" | "elevated" | "outlined" | "glass";
export type PaperPadding = "none" | "sm" | "md" | "lg";

interface PaperOwnerState {
  variant: PaperVariant;
  padding: PaperPadding;
}

/* Exact compiled output of the pre-migration Tailwind utilities plus the
 * shared `.bfrs-faded-card-border` component-layer rules (copied verbatim
 * from Card — the global class keeps shipping until every dependent component
 * is migrated). Verified against the frozen baseline. Note: `duration-200`
 * sits after `transition-shadow` in both the class list and the compiled
 * stylesheet, so the net transition-duration is .2s. */

const fadedCardBorder: CSSObject = {
  position: "relative",
  border: "1px solid transparent",
  isolation: "isolate",
  "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border)",
  "&:before": {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    padding: "1px",
    pointerEvents: "none",
    content: '""',
    background: "var(--bfrs-card-border-gradient)",
    borderRadius: "inherit",
    WebkitMask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    mask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude"
  },
  "&:hover": {
    "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border-hover)"
  }
};

const paperBase: CSSObject = {
  borderRadius: "var(--bfrs-radius-xl)",
  transitionProperty: "box-shadow",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".2s"
};

const paperVariantStyles: Record<PaperVariant, CSSObject> = {
  default: {
    ...fadedCardBorder,
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    "--tw-shadow": "var(--bfrs-shadow-card)",
    "--tw-shadow-colored": "var(--bfrs-shadow-card)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  },
  muted: {
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
  },
  elevated: {
    ...fadedCardBorder,
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    "--tw-shadow": "var(--bfrs-shadow-elevated)",
    "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  },
  outlined: {
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    "--tw-shadow": "0 0 #0000",
    "--tw-shadow-colored": "0 0 #0000",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  },
  glass: {
    ...fadedCardBorder,
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / .8)",
    "--tw-shadow": "var(--bfrs-shadow-card)",
    "--tw-shadow-colored": "var(--bfrs-shadow-card)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
    "--tw-backdrop-blur": "blur(12px)",
    WebkitBackdropFilter:
      "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)",
    backdropFilter:
      "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)"
  }
};

const paperPaddingStyles: Record<PaperPadding, CSSObject> = {
  none: { padding: 0 },
  sm: { padding: "1rem" },
  md: { padding: "1.25rem" },
  lg: { padding: "1.5rem" }
};

const PaperRoot = styled("div", {
  label: "AgenticPaper-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: PaperOwnerState }>(({ ownerState }) => [
  paperBase,
  paperVariantStyles[ownerState.variant],
  paperPaddingStyles[ownerState.padding],
  ownerState.variant === "outlined" &&
    ownerState.padding === "md" && {
      paddingLeft: "22px",
      paddingRight: "22px",
      paddingTop: "18px",
      paddingBottom: "18px"
    }
]);

export type PaperProps = HTMLAttributes<HTMLDivElement> & {
  variant?: PaperVariant | null;
  padding?: PaperPadding | null;
};

export const Paper = forwardRef<HTMLDivElement, PaperProps>(({ className, variant, padding, ...props }, ref) => (
  <PaperRoot
    ref={ref}
    ownerState={{ variant: variant ?? "default", padding: padding ?? "md" }}
    className={className}
    {...props}
  />
));

Paper.displayName = "Paper";
